import { Id } from '@/infra/id'
import type { GetCurrentMemberFn } from '@/features/iam'
import { type BillingApi } from '../api/billing-api'
import { type Coupon } from './Coupon'
import {
  mapUserCouponResourceToCoupon,
  mapCouponResourceToCoupon,
} from '@/features/billing/coupons/coupon-mappers'

/**
 * Store for managing and retrieving a user's coupons.
 */
export class CouponStore {
  constructor(
    private readonly _api: BillingApi,
    private readonly _getCurrentMember: GetCurrentMemberFn,
  ) {}

  /**
   * Lists all active (non-redeemed, non-expired) coupons for the current user.
   */
  async listCoupons(signal?: AbortSignal): Promise<Coupon[]> {
    const member = this._getCurrentMember()
    const workspaceId = Id.assert(member.workspaceId)
    const userId = Id.assert(member.userId)

    const resources = await this._api.userCoupons.getCouponsForUser(
      workspaceId,
      userId,
      signal,
    )

    return resources
      .filter((r) => !r.redeemed)
      .map(mapUserCouponResourceToCoupon)
      .filter((c) => !c.isExpired)
  }

  /**
   * Adds a new coupon by code to the current user's profile.
   */
  async addCoupon(code: string): Promise<Coupon> {
    const member = this._getCurrentMember()
    const resource = await this._api.userCoupons.addCouponToUser(code, {
      workspace_id: Id.assert(member.workspaceId),
      user_public_id: Id.assert(member.userId),
    })

    return mapUserCouponResourceToCoupon(resource)
  }

  /**
   * Fetches a coupon by its ID.
   */
  async fetchCoupon(id: string, signal?: AbortSignal): Promise<Coupon> {
    const resource = await this._api.coupons.getCoupon(id, signal)
    return mapCouponResourceToCoupon(resource)
  }
}
